import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin, Pencil, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { AisleWalkthrough } from '../types';
import {
  resolveTaggedRetailerLocations,
  type RetailerLocationOption,
} from '../lib/inStoreLocationLabels';

interface WalkthroughAisleTagsPanelProps {
  walk: AisleWalkthrough;
  storeId: number;
  storeIdParam: string;
  retailerInstoreLocations: RetailerLocationOption[];
  onFocusAisle?: (aisleNumber: number | null) => void;
  onUpdated?: () => void;
}

export function WalkthroughAisleTagsPanel({
  walk,
  storeId,
  storeIdParam,
  retailerInstoreLocations,
  onUpdated,
}: WalkthroughAisleTagsPanelProps) {
  const walkthroughId = walk.liveWalkthroughId;
  const sortedLocations = useMemo(
    () => [...retailerInstoreLocations].sort((a, b) => a.name.localeCompare(b.name)),
    [retailerInstoreLocations],
  );
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(
    walk.instoreLocationIds ?? [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (!isEditing) {
      setSelectedLocationIds(walk.instoreLocationIds ?? []);
    }
  }, [walk.instoreLocationIds, walk.id, isEditing]);

  const tagged = resolveTaggedRetailerLocations(walk.instoreLocationIds, sortedLocations);

  const toggleLocation = (locationId: number, checked: boolean) => {
    setSelectedLocationIds((current) => {
      if (checked) {
        return current.includes(locationId) ? current : [...current, locationId];
      }
      return current.filter((value) => value !== locationId);
    });
  };

  const invalidateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'walkthroughs'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
    ]);
  };

  const handleSave = async () => {
    if (walkthroughId == null) return;
    if (selectedLocationIds.length === 0) {
      toast({
        title: 'Tag at least one location',
        description: 'Pick from the retailer in-store location catalog.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}`,
        'PATCH',
        { instoreLocationIds: [...selectedLocationIds].sort((a, b) => a - b) },
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to update locations');
      }
      await invalidateQueries();
      setIsEditing(false);
      onUpdated?.();
      toast({ title: 'Locations updated' });
    } catch (error) {
      toast({
        title: 'Could not save locations',
        description: error instanceof Error ? error.message : 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (walkthroughId == null) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium flex items-center gap-1.5">
          <MapPin className="h-4 w-4" />
          In-store locations
        </p>
        <div className="flex flex-wrap gap-1.5">
          {tagged.length === 0 ? (
            <p className="text-sm text-gray-500">No locations tagged.</p>
          ) : (
            tagged.map((item) => (
              <Badge key={item.id} variant="secondary">{item.label}</Badge>
            ))
          )}
        </div>
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            In-store locations
          </p>
          <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Tag locations
          </Button>
        </div>
        {tagged.length === 0 ? (
          <p className="text-sm text-gray-500">
            No locations tagged yet. Tag from the retailer in-store location catalog.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tagged.map((item) => (
              <Badge key={item.id} variant="secondary">
                {item.label}
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border bg-gray-50/80 p-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5">
          <MapPin className="h-4 w-4" />
          Tag in-store locations
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsEditing(false);
            setSelectedLocationIds(walk.instoreLocationIds ?? []);
          }}
        >
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
      </div>

      {sortedLocations.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border rounded-lg p-3 bg-white">
          {sortedLocations.map((location) => (
            <label key={location.id} className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={selectedLocationIds.includes(location.id)}
                onCheckedChange={(checked) => toggleLocation(location.id, checked === true)}
                className="mt-0.5"
              />
              <span className="font-medium">{location.name}</span>
            </label>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          No retailer in-store locations defined yet. Add them under retailer lookup tables first.
        </p>
      )}

      {selectedLocationIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {resolveTaggedRetailerLocations(selectedLocationIds, sortedLocations).map((item) => (
            <Badge key={item.id} variant="outline">
              {item.label}
            </Badge>
          ))}
        </div>
      )}

      <Button
        type="button"
        size="sm"
        disabled={isSaving || sortedLocations.length === 0}
        onClick={() => void handleSave()}
      >
        {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
        Save locations
      </Button>
    </div>
  );
}
